import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SharedTable from '../../../../../shared/Table/SharedTable';
import UnifiedDateRangePicker from './UnifiedDateRangePicker';
import { formatDate, formatTime } from '../../../../../../utils/dateUtils';
import '../../../../../../components/shared/filterStyles.css';

/**
 * JSDoc for JournalDetailView component.
 * 
 * @component
 * @param {Object} props - Component properties.
 * @param {Array} props.data - List of journal entry objects.
 * @param {boolean} props.isLoading - Loading state indicator.
 * @param {Object} props.project - The current project object.
 * @returns {React.ReactElement} The journal detail view.
 */
export default function JournalDetailView({ data = [], isLoading = false, project }) {
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState('');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });

    const hasActiveFilters = !!(searchText || dateRange.start || dateRange.end);

    const filteredData = useMemo(() => {
        return data.filter(item => {
            let matchesText = true;
            if (searchText) {
                const searchLower = searchText.toLowerCase();
                const titleText = (item.title || 'Tagesbericht').toLowerCase();
                matchesText = (
                    titleText.includes(searchLower) ||
                    (item.content && item.content.toLowerCase().includes(searchLower)) ||
                    (item.user_name && item.user_name.toLowerCase().includes(searchLower))
                );
            }

            let matchesDate = true;
            if (dateRange.start || dateRange.end) {
                const itemDate = new Date(item.report_date);
                itemDate.setHours(0, 0, 0, 0);

                if (dateRange.start) {
                    const start = new Date(dateRange.start);
                    start.setHours(0, 0, 0, 0);
                    matchesDate = matchesDate && itemDate >= start;
                }
                if (dateRange.end) {
                    const end = new Date(dateRange.end);
                    end.setHours(23, 59, 59, 999);
                    matchesDate = matchesDate && itemDate <= end;
                }
            }

            return matchesText && matchesDate;
        });
    }, [data, searchText, dateRange]);

    const handleReset = () => {
        setSearchText('');
        setDateRange({ start: '', end: '' });
    };

    const columns = [
        {
            key: 'report_date',
            label: 'Datum',
            sortable: true,
            render: (je) => {
                const latestTimestamp = je.updated_at || je.created_at || je.report_date;
                const dStr = formatDate(latestTimestamp);
                const tStr = formatTime(latestTimestamp);
                return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span>{dStr}</span>
                        {tStr && tStr !== '00:00' && (
                            <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>{tStr} Uhr</span>
                        )}
                    </div>
                );
            },
            className: 'date-cell'
        },
        {
            key: 'title',
            label: 'Bericht / Titel',
            sortable: true,
            render: (je) => je.title || 'Tagesbericht',
            className: 'bold-cell'
        },
        {
            key: 'user_name',
            label: 'Verfasser',
            sortable: true
        },
        {
            key: 'content',
            label: 'Inhalt',
            sortable: true,
            render: (je) => (
                <div className="notes-cell journal-content-preview">
                    {je.content || <span className="text-muted">Kein Inhalt</span>}
                </div>
            )
        }
    ];

    const handleRowClick = (item) => {
        if (project?.id) {
            sessionStorage.setItem('projectModalBackup', JSON.stringify({
                isOpen: true,
                activeTab: 'analysis',
                currentMode: 'view',
                project: project
            }));
            sessionStorage.setItem('projectAnalysis_activeSubView', 'journal');
            navigate(`/tools/journal/${project.id}`, {
                state: {
                    from: window.location.pathname,
                    focusEntryId: item.id
                }
            });
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="co-filters-panel">
                <div className="co-filter-group">
                    <label>Suche</label>
                    <input
                        type="text"
                        className="co-filter-input"
                        placeholder="Nach Titel, Inhalt oder Autor filtern..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>

                <UnifiedDateRangePicker
                    startDate={dateRange.start}
                    endDate={dateRange.end}
                    onChange={setDateRange}
                />

                <button
                    type="button"
                    className={`co-filter-reset-btn ${!hasActiveFilters ? 'disabled' : ''}`}
                    disabled={!hasActiveFilters}
                    onClick={handleReset}
                    title="Filter zurücksetzen"
                >
                    🔄
                </button>
            </div>

            <div className="subview-content-list glass-card">
                <SharedTable
                    data={filteredData}
                    columns={columns}
                    uniqueKeyField="id"
                    onRowClick={handleRowClick}
                    isLoading={isLoading}
                    emptyMessage="Keine Bautagebuch-Logs für die gewählten Filter gefunden."
                />
            </div>
        </div>
    );
}
